import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from './Header';
import Footer from './Footer';
import NewsletterSubscription from './NewsletterSubscription';

const HomePage = () => {
  const { category, tag } = useParams();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState(category || 'all');
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 6;
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch posts based on category if provided
        let postsResponse;
        if (category) {
          postsResponse = await axios.get(`/api/categories/${category}/posts`);
          setActiveCategory(category);
        } else if (tag) {
          // Fetch all posts and filter by tag on client side
          // In a real implementation, you might want a dedicated API endpoint for this
          postsResponse = await axios.get('/api/posts');
          postsResponse.data = postsResponse.data.filter(post => 
            post.tags && post.tags.includes(tag)
          );
        } else {
          postsResponse = await axios.get('/api/posts');
        }
        
        // Fetch categories with post counts
        const categoriesResponse = await axios.get('/api/categories/with-counts');
        
        setPosts(postsResponse.data);
        setCategories(categoriesResponse.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load content. Please try again later.');
        setLoading(false);
      }
    };

    fetchData();
  }, [category, tag]);
  
  // Update active category when route param changes
  useEffect(() => {
    if (category) {
      setActiveCategory(category);
    } else if (!tag) {
      setActiveCategory('all');
    }
  }, [category, tag]);
  
  // Filter posts based on search term and category
  const filteredPosts = posts.filter(post => {
    const matchesSearch = searchTerm === '' || 
                         post.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         post.content.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = activeCategory === 'all' || post.category === activeCategory;
    
    return matchesSearch && (tag ? true : matchesCategory);
  });
  
  // Get unique categories from API response
  const categoryOptions = ['all', ...categories.map(cat => cat.name)];
  
  // Pagination
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  
  // Featured post is the most recent one
  const featuredPost = posts.length > 0 ? posts[0] : null;
  
  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on new search
  };
  
  // Handle category filter change
  const handleCategoryChange = (category) => {
    if (category === 'all') {
      navigate('/'); // Go to home page without category filter
    } else {
      navigate(`/categories/${category}`); // Navigate to category-specific route
    }
    setActiveCategory(category);
    setCurrentPage(1); // Reset to first page on category change
  };
  
  // Handle pagination
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  
  if (loading) {
    return (
      <>
        <Header />
        <div className="container mx-auto py-12">
          <div className="flex justify-center items-center h-64">
            <div className="spinner"></div>
          </div>
        </div>
        <Footer />
      </>
    );
  }
  
  if (error) {
    return (
      <>
        <Header />
        <div className="container mx-auto py-12">
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
            <p className="text-red-700">{error}</p>
          </div>
          <div className="text-center mt-8">
            <button 
              onClick={() => window.location.reload()}
              className="btn btn-primary"
            >
              Try Again
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }
  
  return (
    <>
      <Header />
      
      {/* Hero Section */}
      <div className="hero-section">
        <div className="container mx-auto px-4 text-center">
          <h1 className="hero-title">CoreLinkAI Blog</h1>
          <p className="hero-subtitle">Insights and innovations at the intersection of artificial intelligence and business transformation</p>
          <div className="flex justify-center mt-8">
            <a href="#latest-posts" className="btn btn-secondary mr-4">
              Read Latest Posts
            </a>
            <Link to="/categories" className="btn btn-outline bg-white/10">
              Browse Categories
            </Link>
          </div>
        </div>
      </div>
      
      {/* Featured Post Section */}
      {featuredPost && !category && !tag && (
        <div className="bg-white py-12">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-secondary-800 font-heading">Featured Article</h2>
              <div className="w-16 h-1 bg-primary-500 mx-auto mt-4"></div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-center">
              <div className="lg:col-span-3 order-2 lg:order-1">
                <div className="category-badge inline-block mb-4">{featuredPost.category}</div>
                <h3 className="text-3xl font-bold text-secondary-800 font-heading mb-4">{featuredPost.title}</h3>
                <p className="text-secondary-600 mb-4 text-lg">{featuredPost.content.substring(0, 250)}...</p>
                <div className="flex items-center mb-6">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold mr-3">
                    {featuredPost.author.replace(/^Dr\.?\s+/i, "").charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-secondary-800">{featuredPost.author}</p>
                    <p className="text-sm text-secondary-500">{new Date(featuredPost.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                  </div>
                </div>
                <Link to={`/post/${featuredPost.slug || featuredPost._id}`} className="btn btn-primary">
                  Read Full Article
                </Link>
              </div>
              <div className="lg:col-span-2 order-1 lg:order-2">
                {featuredPost.imageUrl ? (
                  <img 
                    src={featuredPost.imageUrl} 
                    alt={featuredPost.title} 
                    className="w-full h-64 lg:h-80 object-cover rounded-lg shadow-lg" 
                  />
                ) : (
                  <div className="w-full h-64 lg:h-80 bg-gradient-to-r from-primary-400 to-primary-600 rounded-lg shadow-lg flex items-center justify-center">
                    <span className="text-white text-5xl font-bold opacity-30 font-heading">CoreLinkAI</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Latest Posts Section */}
      <div id="latest-posts" className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-secondary-800 font-heading">
              {category ? `${category} Articles` : 
               tag ? `Articles Tagged #${tag}` : 
               'Latest Insights'}
            </h2>
            <div className="w-16 h-1 bg-primary-500 mx-auto mt-4"></div>
            <p className="text-secondary-600 max-w-2xl mx-auto mt-4">
              {category ? `Explore our articles about ${category}` :
               tag ? `Explore articles related to #${tag}` :
               'Explore our latest articles on artificial intelligence, machine learning, and digital transformation'}
            </p>
          </div>
          
          {/* Search Bar */}
          <div className="search-bar">
            <span className="search-icon">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
              </svg>
            </span>
            <input 
              type="text" 
              className="search-input" 
              placeholder="Search articles..." 
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
          
          {/* Category Filter */}
          {!category && !tag && (
            <div className="category-filter">
              {categoryOptions.map((categoryName, index) => (
                <button
                  key={index}
                  className={`category-filter-item ${activeCategory === categoryName ? 'active' : ''}`}
                  onClick={() => handleCategoryChange(categoryName)}
                >
                  {categoryName === 'all' ? 'All Categories' : categoryName}
                  {categoryName !== 'all' && (
                    <span className="ml-2 text-xs bg-gray-200 text-gray-700 rounded-full px-2 py-1">
                      {categories.find(c => c.name === categoryName)?.count || 0}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
          
          {currentPosts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-lg text-gray-600">No posts found matching your criteria. Try adjusting your search or filters.</p>
              {(category || tag) && (
                <Link to="/" className="btn btn-outline mt-4">
                  View All Posts
                </Link>
              )}
            </div>
          ) : (
            <>
              <div className="post-grid">
                {currentPosts.map(post => (
                  <div key={post._id} className="card group">
                    <div className="relative">
                      {post.imageUrl ? (
                        <img 
                          src={post.imageUrl} 
                          alt={post.title} 
                          className="card-img" 
                        />
                      ) : (
                        <div className="w-full h-52 bg-gradient-to-r from-primary-100 to-primary-200 flex items-center justify-center">
                          <span className="text-primary-500 text-4xl font-bold opacity-30">CoreLinkAI</span>
                        </div>
                      )}
                      <div className="absolute top-4 right-4">
                        <span className="category-badge">{post.category}</span>
                      </div>
                    </div>
                    <div className="card-content">
                      <h3 className="card-title">{post.title}</h3>
                      <p className="card-meta">
                        <span className="mr-2">{new Date(post.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        <span>By {post.author}</span>
                      </p>
                      <p className="card-excerpt">{post.content.substring(0, 150)}...</p>
                      <Link to={`/post/${post.slug || post._id}`} className="card-link">
                        Read more
                        <svg className="w-3.5 h-3.5 ml-1 inline-block transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                        </svg>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  {currentPage > 1 && (
                    <button 
                      onClick={() => paginate(currentPage - 1)}
                      className="pagination-item"
                      aria-label="Previous page"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                      </svg>
                    </button>
                  )}
                  
                  {[...Array(totalPages)].map((_, index) => (
                    <button
                      key={index}
                      onClick={() => paginate(index + 1)}
                      className={`pagination-item ${currentPage === index + 1 ? 'active' : ''}`}
                    >
                      {index + 1}
                    </button>
                  ))}
                  
                  {currentPage < totalPages && (
                    <button 
                      onClick={() => paginate(currentPage + 1)}
                      className="pagination-item"
                      aria-label="Next page"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                      </svg>
                    </button>
                  )}
                </div>
              )}
            </>
          )}
          
          {!category && !tag && (
            <div className="text-center mt-12">
              <Link to="/archive" className="btn btn-outline">View All Articles</Link>
            </div>
          )}
        </div>
      </div>
      
      {/* Newsletter Section */}
      <NewsletterSubscription />
      
      <Footer />
    </>
  );
};

export default HomePage;
